import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useLang, t } from "@/context/LangContext";
import { Link, useParams } from "react-router-dom";
import { BookOpen, Clock, ArrowLeft } from "lucide-react";

const EDU_IMG = "https://images.pexels.com/photos/7012265/pexels-photo-7012265.jpeg";

const CATS = [
  { id: "all", label_id: "Semua", label_en: "All" },
  { id: "beginner", label_id: "Pemula", label_en: "Beginner" },
  { id: "intermediate", label_id: "Menengah", label_en: "Intermediate" },
  { id: "advanced", label_id: "Lanjutan", label_en: "Advanced" },
];

export const EducationList = () => {
  const { lang } = useLang();
  const [articles, setArticles] = useState([]);
  const [cat, setCat] = useState("all");

  useEffect(() => {
    const q = { params: {} };
    if (cat !== "all") q.params.category = cat;
    api.get("/education", q).then((r) => setArticles(r.data.articles || []));
  }, [cat]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12" data-testid="education-page">
      <div className="overline text-white/40 mb-3">{t(lang, "Perpustakaan Edukasi", "Education Library")}</div>
      <h1 className="font-display font-black text-5xl md:text-6xl tracking-tighter mb-8">
        {t(lang, "Belajar Investasi", "Learn to Invest")}
      </h1>

      <div className="inline-flex border border-white/10 rounded-md overflow-hidden mb-10 flex-wrap" data-testid="edu-filter">
        {CATS.map((c) => (
          <button
            key={c.id}
            onClick={() => setCat(c.id)}
            data-testid={`edu-cat-${c.id}`}
            className={`px-5 py-2 text-sm font-medium transition-colors ${cat === c.id ? "bg-white text-black" : "text-white/60 hover:text-white hover:bg-white/5"}`}
          >
            {t(lang, c.label_id, c.label_en)}
          </button>
        ))}
      </div>

      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
        {articles.map((a) => (
          <Link
            key={a.slug}
            to={`/education/${a.slug}`}
            data-testid={`article-${a.slug}`}
            className="group border border-white/10 rounded-2xl overflow-hidden hover:border-white/25 transition-colors bg-[#0d0d0d]"
          >
            <div className="h-40 overflow-hidden relative">
              <img src={EDU_IMG} alt="" className="w-full h-full object-cover opacity-40 group-hover:opacity-60 transition-opacity" />
              <div className="absolute inset-0 bg-gradient-to-t from-black to-transparent" />
              <div className="absolute bottom-3 left-4 flex items-center gap-3 text-xs">
                <span className="overline px-2 py-0.5 rounded bg-white/10 text-white">{a.level}</span>
                <span className="flex items-center gap-1 text-white/60"><Clock size={11} />{a.read_minutes} min</span>
              </div>
            </div>
            <div className="p-6">
              <BookOpen size={16} className="mb-3 text-white/50" />
              <h3 className="font-display font-bold text-xl leading-tight mb-2">{t(lang, a.title_id, a.title_en)}</h3>
              <p className="text-sm text-white/60 leading-relaxed">{t(lang, a.excerpt_id, a.excerpt_en)}</p>
            </div>
          </Link>
        ))}
      </div>
    </div>
  );
};

export const EducationDetail = () => {
  const { slug } = useParams();
  const { lang } = useLang();
  const [article, setArticle] = useState(null);

  useEffect(() => {
    api.get(`/education/${slug}`).then((r) => setArticle(r.data));
  }, [slug]);

  if (!article) return <div className="max-w-3xl mx-auto px-6 py-16 text-white/60">Loading...</div>;

  const content = lang === "id" ? article.content_id : article.content_en;
  const title = lang === "id" ? article.title_id : article.title_en;

  return (
    <article className="max-w-3xl mx-auto px-6 lg:px-10 py-12" data-testid="article-detail">
      <Link to="/education" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-8">
        <ArrowLeft size={16} /> {t(lang, "Kembali ke Perpustakaan", "Back to Library")}
      </Link>
      <div className="overline text-white/40 mb-3">{article.level} · {article.read_minutes} min</div>
      <h1 className="font-display font-black text-4xl md:text-5xl tracking-tighter leading-[1.05] mb-8">{title}</h1>
      <div className="prose prose-invert prose-lg max-w-none">
        {content.split("\n").map((line, idx) => {
          if (line.startsWith("## ")) return <h2 key={idx} className="font-display font-bold text-2xl mt-10 mb-4">{line.replace("## ", "")}</h2>;
          if (line.startsWith("- ")) return <li key={idx} className="text-white/80 ml-6 leading-relaxed">{line.replace("- ", "")}</li>;
          if (line.match(/^\d+\. /)) return <div key={idx} className="text-white/80 ml-6 my-2 leading-relaxed">{line}</div>;
          if (line.trim() === "") return <div key={idx} className="h-4" />;
          return <p key={idx} className="text-white/80 leading-relaxed my-3">{line}</p>;
        })}
      </div>
    </article>
  );
};
